import { useState } from "react";
import { Plus, Trash2, Eye, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { InfoWindow, LAYOUTS } from "@/threedvista/InfoWindow";

const CFG = {
  tabs: { item: "tab", a: "Tab name, e.g. Overview", b: "Tab text" },
  split: { item: "key feature", a: "Feature, e.g. Sea view", b: "Optional detail" },
  list: { item: "item", a: "Item, e.g. Breakfast included", b: "Short description" },
  numbers: { item: "figure", v: "Number, e.g. 2,400", a: "Label, e.g. sq ft" },
  visit: { item: "detail", a: "Label, e.g. Hours", b: "e.g. Mon–Fri · 9am–5pm" },
};

function Items({ w, set, tid }) {
  const c = CFG[w.layout];
  if (!c) return null;
  const items = w.items || [];
  const upd = (i, k, v) => set({ ...w, items: items.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 items-start" data-testid={`${tid}-item-${i}`}>
          {c.v && <Input className="h-9 text-sm w-32 shrink-0" value={it.value || ""} maxLength={20} placeholder={c.v} onChange={(e) => upd(i, "value", e.target.value)} data-testid={`${tid}-item-${i}-value`} />}
          <div className="flex-1 space-y-1">
            <Input className="h-9 text-sm" value={it.title || ""} maxLength={80} placeholder={c.a} onChange={(e) => upd(i, "title", e.target.value)} data-testid={`${tid}-item-${i}-title`} />
            {c.b && <textarea rows={2} className="w-full rounded-md border px-3 py-2 text-sm" value={it.text || ""} maxLength={800} placeholder={c.b} onChange={(e) => upd(i, "text", e.target.value)} data-testid={`${tid}-item-${i}-text`} />}
          </div>
          <button type="button" onClick={() => set({ ...w, items: items.filter((_, j) => j !== i) })} className="w-9 h-9 shrink-0 rounded-lg text-rose-600 hover:bg-rose-50 grid place-items-center" data-testid={`${tid}-item-${i}-delete`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {items.length < 8 && <button type="button" onClick={() => set({ ...w, items: [...items, {}] })} className="text-xs font-semibold text-slate-600 inline-flex items-center gap-1" data-testid={`${tid}-item-add`}><Plus className="w-3.5 h-3.5" />Add {c.item}</button>}
    </div>
  );
}

function Preview({ pin, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-slate-950/70 p-4" onClick={onClose} data-testid="tdv-win-preview">
      <div className="relative w-[min(60rem,95vw)] h-[min(38rem,85vh)] rounded-3xl overflow-hidden bg-gradient-to-br from-slate-500 via-slate-700 to-slate-900" onClick={(e) => e.stopPropagation()}>
        <p className="absolute top-4 left-5 text-xs font-semibold uppercase tracking-widest text-white/60">Preview</p>
        <button type="button" onClick={onClose} className="absolute top-3 right-3 z-50 h-9 px-3 rounded-full bg-white text-slate-900 text-xs font-semibold inline-flex items-center gap-1" data-testid="tdv-win-preview-close"><X className="w-3.5 h-3.5" />Close preview</button>
        <InfoWindow pin={pin} onClose={onClose} onBook={() => {}} />
      </div>
    </div>
  );
}

export function WindowEditor({ pin, set, tid }) {
  const w = pin.win || {};
  const layout = w.layout || "classic";
  const [show, setShow] = useState(false);
  return (
    <div className="rounded-xl bg-slate-50 border p-3 space-y-2" data-testid={`${tid}-win`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500">Info window</span>
        <select value={layout} onChange={(e) => set({ ...w, layout: e.target.value })} className="h-9 flex-1 min-w-[9rem] rounded-lg border px-2 text-sm bg-white" data-testid={`${tid}-win-layout`}>{LAYOUTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select value={w.theme || "dark"} onChange={(e) => set({ ...w, theme: e.target.value })} className="h-9 rounded-lg border px-2 text-sm bg-white" data-testid={`${tid}-win-theme`}><option value="dark">Dark</option><option value="light">Light</option></select>
        <button type="button" onClick={() => setShow(true)} className="h-9 px-3 rounded-lg border bg-white text-sm font-semibold inline-flex items-center gap-1.5 hover:border-slate-400" data-testid={`${tid}-win-preview`}><Eye className="w-4 h-4" />Preview</button>
      </div>
      <Items w={{ ...w, layout }} set={set} tid={`${tid}-win`} />
      {(layout === "banner" || layout === "visit" || layout === "classic") && <Input className="h-9 text-sm" value={w.cta_label || ""} maxLength={30} placeholder={layout === "visit" ? "Button text (default: Book a visit, opens the enquiry form)" : "Button text (uses the link above, or opens the enquiry form)"} onChange={(e) => set({ ...w, cta_label: e.target.value })} data-testid={`${tid}-win-cta`} />}
      {show && <Preview pin={{ ...pin, win: { ...w, layout } }} onClose={() => setShow(false)} />}
    </div>
  );
}
